import { useCallback, useEffect, useRef, useState } from "react";
import { type GraphData } from "../lib/api";

const HISTORY_LIMIT = 80;
const HISTORY_DEBOUNCE_MS = 320;

export const useGraphHistory = () => {
  const [history, setHistory] = useState<GraphData[]>([]);
  const [index, setIndex] = useState(-1);
  const [lockIndex, setLockIndex] = useState<number | null>(null);
  const indexRef = useRef(index);
  const historyRef = useRef(history);
  const debounceRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  const push = useCallback((snapshot: GraphData) => {
    setHistory((prev) => {
      const trimmed = prev.slice(0, indexRef.current + 1);
      const next = [...trimmed, snapshot].slice(-HISTORY_LIMIT);
      const nextIndex = next.length - 1;
      indexRef.current = nextIndex;
      setIndex(nextIndex);
      return next;
    });
  }, []);

  const canUndoBase = index > 0;
  const canRedo = history.length > 0 && index < history.length - 1;

  const undo = useCallback(() => {
    const minIndex = lockIndex ?? 0;
    if (indexRef.current <= minIndex) return null;
    const nextIndex = indexRef.current - 1;
    indexRef.current = nextIndex;
    setIndex(nextIndex);
    return historyRef.current[nextIndex];
  }, [lockIndex]);

  const redo = useCallback(() => {
    if (indexRef.current >= historyRef.current.length - 1) return null;
    const nextIndex = indexRef.current + 1;
    indexRef.current = nextIndex;
    setIndex(nextIndex);
    return historyRef.current[nextIndex];
  }, []);

  const reset = useCallback((snapshot: GraphData) => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
    historyRef.current = [snapshot];
    indexRef.current = 0;
    setLockIndex(0);
    setHistory([snapshot]);
    setIndex(0);
  }, []);

  const queue = useCallback(
    (snapshot: GraphData, delay = HISTORY_DEBOUNCE_MS) => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        debounceRef.current = undefined;
        push(snapshot);
      }, delay);
    },
    [push]
  );

  const cancelQueue = useCallback(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
  }, []);

  const markRemote = useCallback(() => {
    setLockIndex(indexRef.current);
  }, []);

  const canUndo = lockIndex !== null ? index > lockIndex : canUndoBase;

  return { push, queue, cancelQueue, reset, undo, redo, canUndo, canRedo, markRemote, lockIndex };
};
